import { DialogResults, useTranslation } from '@msdk/common'
import { Card, CardHeader, Chip, styled, uiTokens } from '@msdk/ui'
import { CreditCardOutlined, LockOutlined, PhotoCameraOutlined } from '@mui/icons-material'
import { CardContent } from '@mui/material'
import { ComponentType } from 'react'
import { useWizard } from 'react-use-wizard'

import { FooterPopup } from './FooterPopup'
import { HeaderPopup } from './HeaderPopup'
import { ModalStepProps, StyledContent, StyledMessageTypography } from './shared'

type OptionDefinition = {
  icon: ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>
  title: string
  description: string
  label: string
}

const StyledOptionCard = styled(Card)`
  &.MuiPaper-root {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${uiTokens.spacing.gap.semiMedium}px;
    width: 100%;
    box-sizing: border-box;
    padding: ${uiTokens.spacing.padding.medium}px;
    border-radius: ${uiTokens.radius.large}px;
    border: ${uiTokens.sizing.stroke.s}px solid ${uiTokens.uiStroke.neutral};
    box-shadow: none;
  }
`

const StyledOptionIconWrap = styled('div')`
  align-items: center;
  width: 32px;
  height: 32px;
  color: ${uiTokens.uiContent.emphasized};
`

const StyledOptionBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${uiTokens.spacing.gap.xSmall}px;
  width: 100%;
`

const StyledOptionHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${uiTokens.spacing.gap.small}px;
  width: 100%;
`

const StyledOptionTitle = styled(CardHeader)`
  padding: 0;
`

const StyledOptionDescription = styled(CardContent)`
  color: ${uiTokens.uiContent.neutral};
  font-size: ${uiTokens.font.fontSize.body.medium}px;
  line-height: ${uiTokens.font.lineHeight.body.medium}px;
  padding: 0;
  margin: 0;
  &:last-child {
    padding-bottom: 0;
  }
`

const StyledOptionChip = styled(Chip)`
  color: ${uiTokens.uiContent.emphasized};
  background-color: ${uiTokens.uiControl.contained.readonly};
  font-size: ${uiTokens.font.fontSize.body.small}px;
  font-weight: ${uiTokens.font.fontWeight.medium}px;
  line-height: ${uiTokens.font.lineHeight.uiControlText.small}px;
  letter-spacing: ${uiTokens.font.letterSpacing.normal};
`

const renderOption = ({ icon: Icon, title, description, label }: OptionDefinition) => (
  <StyledOptionCard key={title}>
    <StyledOptionIconWrap>
      <Icon fontSize="medium" />
    </StyledOptionIconWrap>
    <StyledOptionBody>
      <StyledOptionHeader>
        <StyledOptionTitle title={title} />
        <StyledOptionChip size="small" label={label} />
      </StyledOptionHeader>
      <StyledOptionDescription>{description}</StyledOptionDescription>
    </StyledOptionBody>
  </StyledOptionCard>
)

export function ChooseSignInMethodPopup(props: ModalStepProps) {
  const { nextStep } = useWizard()
  const t = useTranslation()
  const dialogTitle = t('LBL_NGID_MACHINE_TITLE', 'Dialysis machines access')
  const dialogMessage = t(
    'LBL_NGID_MACHINE_DESCR',
    `Activate and setup available login methods to the dialysis machines at your clinic. Please complete 'required' setup before approaching machine.`,
  )
  const requiredLabel = t('LBL_NGID_REQUIRED_METHOD', 'Required')
  const optionalLabel = t('LBL_NGID_OPTIONAL_METHOD', 'Optional')
  const options: OptionDefinition[] = [
    {
      icon: LockOutlined,
      title: t('LBL_NGID_USERNAME_PASSWORD_TITLE', 'Username and password'),
      description: t('LBL_NGID_USERNAME_PASSWORD_DESCR', 'Basic dialysis machine login method.'),
      label: requiredLabel,
    },
    {
      icon: PhotoCameraOutlined,
      title: t('LBL_NGID_FACE_RECO_TITLE', 'Face recognition'),
      description: t('LBL_NGID_FACE_RECO_DESCR', 'Touch-less biometric login.'),
      label: optionalLabel,
    },
    {
      icon: CreditCardOutlined,
      title: t('LBL_NGID_NFC_CARD_TITLE', 'NFC card'),
      description: t('LBL_NGID_NFC_CARD_DESCR', 'Log in using your card.'),
      label: optionalLabel,
    },
  ]

  return (
    <>
      <HeaderPopup title={dialogTitle} modalRef={props.modalRef} />
      <StyledContent>
        <StyledMessageTypography variant="body1">{dialogMessage}</StyledMessageTypography>
        {options.map(renderOption)}
      </StyledContent>
      <FooterPopup
        secondary={{
          label: t('LBL_NGID_SKIP_BTN', 'Skip'),
          onClick: () => props.modalRef?.close(DialogResults.Ok),
        }}
        primary={{ label: t('LBL_NGID_GET_STARTED_BTN', 'Get started'), onClick: () => nextStep() }}
      />
    </>
  )
}
